
import React from "react";
import { Button, Input, TextArea } from "semantic-ui-react";

import { Utils } from "../../common/utils";
import { Modal } from "../../components";
import SelectMultipleAddressees from "../../components/AddressBook/SelectMultipleAddressees";
import { routes } from "utilities/routing/generatedRoutes";
import ChooseTemplateButton from "components/MessageTemplate/ChooseTemplateButton";
import { useMutation } from "@tanstack/react-query";
import { getInvitationTranslation } from "components/MessageTemplate/hooks";
import AdvancingTemplatePaginatedDropdown from "components/dropdowns/paginated/AdvancingTemplatePaginatedDropdown";
import { PhosphorGearButton, PhosphorManifyingGlassButton } from "components/Atoms/PhosphorIconButton";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { getCultureFromLcid } from "utilities/enumutils";

interface Props {
  showId: number
  onFinishSending: () => void
}

const SendAdvancingRequest = (props: Props) => {
  const [selectedAdvancingTemplate, setSelectedAdvancingTemplate] = React.useState<server.dto.AdvancingFormTemplateForList>(null);
  const [emails, setEmails] = React.useState("");
  const [invitationSubject, setInvitationSubject] = React.useState("");
  const [invitationBody, setInvitationBody] = React.useState("");
  const [culture, setCulture] = React.useState(serverReference.templateCultures.find(c => c.locale === site.culture.uiLocale));

  const [emailError, setEmailError] = React.useState(false);
  const [advancingTemplateError, setAdvancingTemplateError] = React.useState(false);

  const [displayAddressBook, setDisplayAddressBook] = React.useState(false);

  const [defaultTemplateHasBeenChosen, setDefaultTemplateHasBeenChosen] = React.useState(false);

  const advancingTemplateUrl = routes.advancingFormTemplate_List().absolute;

  const hasNoInvitationBody = invitationBody == null || invitationBody.trim() === "";

  const onEmailsChange = async (value: string, firstCulture?: server.dto.Culture) => {
    setEmails(value);
    setEmailError(false);
    setDisplayAddressBook(false);

    if (firstCulture && !defaultTemplateHasBeenChosen && hasNoInvitationBody) {
      setDefaultTemplateHasBeenChosen(true);
      setCulture(firstCulture);

      const defaultTemplateRes = await window.CoreApi.MessageTemplate.PreferredInvitationTemplateTranslation({ type: "invitationForAdvancing", language: firstCulture.language });

      setInvitationSubject(defaultTemplateRes.data.data.subject);
      setInvitationBody(defaultTemplateRes.data.data.body);

    }
  };

  const onCultureChanged = async (culture: server.dto.Culture) => {
    setCulture(culture);
    if (culture && hasNoInvitationBody) {
      setDefaultTemplateHasBeenChosen(true);
      const defaultTemplate = await window.CoreApi.MessageTemplate.PreferredInvitationTemplateTranslation({ type: "invitationForAdvancing", language: culture.language });
      if (defaultTemplate) {
        setInvitationSubject(defaultTemplate.data.data.subject);
        setInvitationBody(defaultTemplate.data.data.body);
      }
    }
  };

  const onEmailsBlur = () => {
    const newEmails = Utils.processManualRecipientsString(emails);
    setEmails(newEmails);
    setEmailError(false);
  };

  const onNewTemplateSelected = (template: server.dto.AdvancingFormTemplateForList) => {
    setAdvancingTemplateError(false);
    setSelectedAdvancingTemplate(template);
  };

  const sendMessageMutation = useMutation(window.CoreApi.Advancing.CreateAdvancingRequest);

  const hasErrors = emailError || advancingTemplateError;

  const onSubmit = () => {
    let hasErrors = false;
    if (emails.trim() === "") {
      setEmailError(true);
      hasErrors = true;
    }

    if (!selectedAdvancingTemplate) {
      setAdvancingTemplateError(true);
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    sendMessageMutation.mutateAsync({
      showId: props.showId,
      recipientEmails: emails,
      templateId: selectedAdvancingTemplate.advancingFormTemplateId,
      invitationSubject: invitationSubject,
      invitationBody: invitationBody,
      culture: culture.lcid
    })
      .then(props.onFinishSending);
  };



  return <>
    <div className="flex-column gap grow1 scrollbox-auto">
      <table className="s1_table bordergrid gray">
        <tbody>
          <tr>
            <td>{RESX.Advancing.AdvancingTemplate}</td>
            <td>
              <div className="flex gap-small">
                <div className="grow1">
                  <AdvancingTemplatePaginatedDropdown
                    value={selectedAdvancingTemplate}
                    onChange={onNewTemplateSelected}
                    config={{
                      hasError: advancingTemplateError
                    }}
                  />
                </div>

                <PhosphorGearButton
                  onClick={() => window.location.href = advancingTemplateUrl}
                  layout="in-square"
                  backgroundColor="light"
                  padding="large"
                />

              </div>
            </td>
          </tr>

          <tr>
            <td>{RESX.Message.lblTo}</td>
            <td>
              <div className="flex gap-small">
                <PhosphorManifyingGlassButton
                  onClick={() => { setDisplayAddressBook(true); }}
                  layout="in-square"
                  backgroundColor="light"
                  padding="large"
                />
                <Input
                  error={emailError}
                  onBlur={onEmailsBlur}
                  placeholder={RESX.GeneralLabels.Email}
                  value={emails}
                  onChange={(_, { value }) => { onEmailsChange(value); }}
                  fluid
                  maxLength={255}
                />
              </div>
            </td>
          </tr>

          <tr>
            <td>{RESX.Language.resxLanguage}</td>
            <td>
              <CulturesTemplateDropdown
                value={culture?.lcid}
                onChange={async (lcid) => {
                  const selectedCulture = getCultureFromLcid(lcid);
                  onCultureChanged(selectedCulture);

                  const preferredTemplate = await window.CoreApi.MessageTemplate.PreferredInvitationTemplateTranslation({ type: "invitationForAdvancing", language: selectedCulture.language });
                  setInvitationSubject(preferredTemplate.data.data.subject ?? "");
                  setInvitationBody(preferredTemplate.data.data.body ?? "");
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div className="flex">
        <div className="grow1"></div>
        <div>
          <ChooseTemplateButton
            type={"invitationForAdvancing"}
            buttonLabel={RESX.InvitationTemplate.resxInvitationTemplate}
            onTemplateSelected={async (template, language) => {
              const translation = await getInvitationTranslation(template.messageTemplateId, language);
              setInvitationSubject(translation.subject);
              setInvitationBody(translation.body);
            }}
          />
        </div>
      </div>
      <div className="flex-column gap-small">
        <div>
          <OptionalTextInput
            value={invitationSubject}
            onChange={setInvitationSubject}
            placeholder={RESX.Message.Subject}
            maxLength={serverReference.constants.messageTemplate.maxTranslationSubjectLength}
          />
        </div>
        <div>
          <TextArea
            className="grow1"
            placeholder={RESX.Invitation.WriteAMessageThatWillBeDisplayed}
            rows={7}
            value={invitationBody}
            maxLength={serverReference.constants.messageTemplate.maxTranslationBodyLength}
            onChange={(_, { value }) => { setInvitationBody(value.toString()); }}
          />
        </div>
      </div>
    </div>

    <div className="flex">
      <div className="grow1"></div>
      <Button
        primary
        type="button"
        disabled={sendMessageMutation.isLoading || hasErrors}
        loading={sendMessageMutation.isLoading}
        onClick={onSubmit}
      >{RESX.Buttons.btnSend}</Button>
    </div>

    <Modal
      isOpen={displayAddressBook}
      handleClose={() => { setDisplayAddressBook(false); }}
      title={RESX.Contact.FindContact}
      width="small"
      minHeight={400}
      height={750}
    >
      <SelectMultipleAddressees
        onEmailsSelected={onEmailsChange}
        initialEmails={emails}
        suggestedApiInstruction={{ itemId: props.showId, mainArea: "show" }}
      />
    </Modal>
  </>;
};

export default SendAdvancingRequest;